<template>
  <b-container fluid>
    <div class="iq-card mt-3 px-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Event Log</h4>
        </div>

        <div class="text-right">
          <button-options :access="'Log_Event_Event'" :hideAdd="true" :hideDetail="true" :isRefreshEnabled="true"
            :isRefreshed="isRefreshed" @onRefreshed="getData('Refreshed')" class="mt-2" />
        </div>
      </div>
      <div class="iq-card-body">
        <div class="d-flex mb-1">
          <div style="margin-right: 10px; width: 300px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Select Range</span>
            <v-date-picker v-model="params.dateRange" is-range mode="datetime" :masks="{ input: 'DD-MM-YYYY HH:mm' }"
              is24hr>
              <template #default="{ inputValue, inputEvents, isDragging }">
                <div class="d-flex flex-column flex-sm-row justify-content-start align-items-center">
                  <input class="form-control bg-light border rounded date-input"
                    :class="isDragging ? 'text-muted' : 'text-dark'" :value="inputValue.start + ' - ' + inputValue.end"
                    v-on="inputEvents.start" />
                </div>
              </template>
            </v-date-picker>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Time</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntTimestamp">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Priority</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntIsPriority">
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Status</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntStatus">
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>
        </div>

        <div class="d-flex mb-2">
          <v-select v-model="params.filter.evntDeviceId" :options="devices" label="text"
            :reduce="device => device.value" placeholder="Select Device" style="width: 270px; margin-right: 10px"
            :clearable=false @search="onSearchDevice" />

          <v-select v-model="params.filter.evntMevtId" :options="eventTypes" label="text"
            :reduce="eventType => eventType.value" placeholder="All Event" style="width: 200px; margin-right: 10px"
            :clearable=false @search="onSearchEventType" />

          <b-form-select v-model="params.filter.evntIsPriority" style="width: 200px; margin-right: 10px">
            <template v-slot:first>
              <option value=''>All Priority</option>
            </template>
            <option value="1">High</option>
            <option value="0">Normal</option>
          </b-form-select>

          <b-form-select v-model="params.filter.evntStatus" style="width: 200px; margin-right: 10px">
            <template v-slot:first>
              <option value=''>All Status</option>
            </template>
            <option value="0">Non Active</option>
            <option value="1">Active</option>
          </b-form-select>

          <button class="btn btn-primary" @click="applyFilter">Apply All</button>
        </div>

        <b-table v-if="params.filter.evntDeviceId && params.filter.evntDeviceId != ''" striped hover fixed
          head-variant="dark" show-empty responsive :busy="isLoading" :fields="fields"
          :items="canRead('log_event_event') ? data : []" style="border-radius: 10px;">
          <!-- Start table busy slot -->
          <template v-slot:table-busy="">
            <div class="text-center text-danger my-2">
              <b-spinner variant="success" size="sm" class="align-middle"></b-spinner>
            </div>
          </template>
          <!-- End table busy slot -->
          <!-- Start empty data slot -->
          <template v-slot:empty>
            <div class="text-center text-muted my-2">
              <strong>No data to show.</strong>
            </div>
          </template>
          <!-- End empty data slot -->

          <!-- Start priority slot -->
          <template v-slot:cell(evntIsPriority)="data">
            <b-badge :variant="data.value == 0 ? 'success' : 'warning'">
              {{ data.value == 0 ? 'Normal' : 'High' }}
            </b-badge>
          </template>
          <!-- End priority slot -->

          <!-- Start status slot -->
          <template v-slot:cell(evntStatus)="data">
            <b-badge :variant="data.value == 1 ? 'success' : 'danger'">
              {{ data.value == 1 ? 'Active' : 'Non Active' }}
            </b-badge>
          </template>
          <!-- End status slot -->

          <!-- Start threshold slot -->
          <template v-slot:cell(action)="data">
            <div class="text-center">
              <button class="btn btn-sm btn-outline-primary mx-1" @click="openDataDetail(data.item)">
                View Data
              </button>

              <button class="btn btn-sm btn-outline-primary mx-1" @click="openThresholdDetail(data.item)">
                View Threshold
              </button>
            </div>
          </template>
          <!-- End threshold slot -->

        </b-table>
        <div v-else class="text-center text-warning my-3">
          <strong>Please select a device to view the event log.</strong>
        </div>
        <b-pagination v-if="data && data.length > 0" v-model="pagination.current" :per-page="pagination.pageSize"
          :total-rows="pagination.totalSize" @change="onPageChanged"></b-pagination>
      </div>
    </div>

    <b-modal size="md" hide-footer no-close-on-esc no-close-on-backdrop v-model="isOpenThresholdDetail">
      <template #modal-header>
        <h5 class="modal-title">Thresholds Data</h5>
        <b-button variant="outline-danger" size="sm" @click="isOpenThresholdDetail = false">
          <i class="ri-close-fill"></i>
        </b-button>
      </template>
      <b-container class="my-4">
        <b-row>
          <b-col md="12" v-for="(item, name) in thresholdDetail" :key="name" class="mb-3">
            <b-card>
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <strong>{{ name }}</strong>
                  <b-badge v-if="item.conditions_operator" variant="info">
                    {{ item.conditions_operator }}
                  </b-badge>
                </div>
              </template>

              <b-table small :items="formatTableData(item.data)" :fields="[
                { key: 'parameter', label: 'Parameter' },
                { key: 'operator', label: 'Operator' },
                { key: 'value', label: 'Value' }
              ]" responsive="sm" bordered head-variant="light" class="mb-0">
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>

    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="isOpenDataDetail">
      <b-table :items="formatedDetailData" :fields="detailDataFields" striped hover small>
        <template #cell(device)="data">
          <strong>{{ data.item.device }}</strong>
        </template>
        <template #cell(type)="data">
          <span class="text-primary">{{ data.item.type }}</span>
        </template>
        <template #cell(dataName)="data">
          {{ data.item.dataName }}
        </template>
        <template #cell(oldValue)="data">
          {{ data.item.oldValue }}
        </template>
        <template #cell(newValue)="data">
          {{ data.item.newValue }}
        </template>
      </b-table>
    </b-modal>
  </b-container>
</template>

<script>
import ButtonOptions from '@/components/directives/button-options';
import EventLogSvc from '@/services/EventLogSvc';
import EventSvc from '@/services/EventSvc';
import DeviceSvc from '@/services/DeviceSvc';
import requestBuilder from '@/util/request.builder';
import { debounce } from 'lodash';
import moment from 'moment';

export default {
  name: 'MasterConfigEvent',
  components: {
    ButtonOptions,
  },
  data() {
    return {
      data: [],
      devices: [],
      eventTypes: [],
      displayForm: false,
      errors: {},
      isLoading: false,
      isRefreshed: false,
      isSubmit: false,
      isOpenThresholdDetail: false,
      isOpenDataDetail: false,
      thresholdDetail: {},
      dataDetail: {},
      params: {
        filter: {
          evntDeviceId: '',
          evntIsPriority: '',
          evntStatus: '',
          evntMevtId: '',
        },
        order: {
          evntIsPriority: '',
          evntStatus: '',
          evntTimestamp: 'desc',
        },
        dateRange: {
          start: moment().startOf('day').toISOString(),
          end: moment().endOf('day').toISOString(),
        },
      },
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 0,
      },
      fields: [
        {
          key: 'evntTimestamp',
          label: 'Date Time',
          class: 'text-left',
          formatter: (value) => {
            const date = moment.utc(value).local();
            return `${date.format('YYYY-MM-DD, HH:mm:ss')}`;
          },
        },
        {
          key: 'evntMevt.mevtType',
          label: 'Event Type',
          class: 'text-left',
          thStyle: { minWidth: '130px' },
        },
        {
          key: 'evntStatus',
          label: 'Status',
          class: 'text-left',
        },
        {
          key: 'evntIsPriority',
          label: 'Priority',
          class: 'text-left',
        },
        {
          key: 'evntDuration',
          label: 'Duration',
          class: 'text-left',
          formatter: (value) => {
            const duration = moment.duration(value, 'seconds');
            return `${duration.hours().toString().padStart(2, '0')}:${duration.minutes().toString().padStart(2, '0')}:${duration.seconds().toString().padStart(2, '0')}`;
          },
        },
        {
          key: 'Action',
          label: 'Detail/Threshold',
          class: 'text-center',
          thStyle: { minWidth: '100px' },
        },
      ],
      detailDataFields: [
        { key: 'device', label: 'Device' },
        { key: 'type', label: 'Type' },
        { key: 'dataName', label: 'Data Name' },
        { key: 'oldValue', label: 'Old Value' },
        { key: 'newValue', label: 'New Value' },
      ],
    };
  },
  computed: {
    formatedDetailData() {
      const { oldData, newData } = this.dataDetail;
      const result = [];

      if (!oldData || !newData) return result;

      const devices = new Set([
        ...Object.keys(oldData),
        ...Object.keys(newData),
      ]);

      devices.forEach((device) => {
        const types = new Set([
          ...Object.keys(oldData[device] || {}),
          ...Object.keys(newData[device] || {}),
        ]);

        types.forEach((type) => {
          const dataNames = new Set([
            ...Object.keys((oldData[device]?.[type]) || {}),
            ...Object.keys((newData[device]?.[type]) || {}),
          ]);

          dataNames.forEach((dataName) => {
            result.push({
              device,
              type,
              dataName,
              oldValue: oldData[device]?.[type]?.[dataName] ?? '-',
              newValue: newData[device]?.[type]?.[dataName] ?? '-',
            });
          });
        });
      });

      return result;
    },
  },
  methods: {
    async getData({
      start,
      end,
      finds,
      orders,
    }) {
      try {
        this.isLoading = true;
        this.isRefreshed = true;

        if (!finds.evntDeviceId) return;
        const Response = await EventLogSvc.getEventLogs({
          start,
          end,
          finds,
          orders,
          params: { ...requestBuilder.buildFilters(this.pagination, {}) },
        });

        const Data = Response.data;
        this.pagination.totalSize = Response.pagination.totalData;

        this.data = Data;
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    async getDevices(deviceId) {
      const params = { 'finds[devcType]': 'gateway' };
      if (deviceId) params['finds[devcUniqueId]'] = deviceId;
      const Response = await DeviceSvc.getDevices({ params });

      this.devices = Response.data.data.map((item) => ({
        value: item.devcUniqueId,
        text: item.devcMass?.massName,
      }));
    },
    async getEventTypes(mevtType) {
      const params = {};
      if (mevtType) params['finds[mevtType]'] = mevtType;
      const Response = await EventSvc.get(params);
      this.eventTypes = Response.data.map((item) => ({
        value: item.mevtId,
        text: item.mevtType,
      }));
    },
    onSearchDevice(searchText) {
      if (searchText) {
        this.getDevices(searchText);
      }
    },
    onSearchEventType(searchText) {
      if (searchText) {
        this.getEventTypes(searchText);
      }
    },
    applyFilter() {
      this.getData({
        start: moment(this.params.dateRange.start).unix(),
        end: moment(this.params.dateRange.end).unix(),
        finds: this.params.filter,
        orders: this.params.order,
      });
    },
    // openDataDetail(data) {
    // },
    openDataDetail(data) {
      this.dataDetail = {
        oldData: JSON.parse(data.evntOldData),
        newData: JSON.parse(data.evntNewData),
      };
      this.isOpenDataDetail = true;
      console.log('this.dataDetail', JSON.stringify(this.dataDetail));
    },
    openThresholdDetail(data) {
      this.thresholdDetail = JSON.parse(data.evntThreshold);
      this.isOpenThresholdDetail = true;
      console.log('this.thresholdDetail', this.thresholdDetail);
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData({
        start: moment(this.params.dateRange.start).unix(),
        end: moment(this.params.dateRange.end).unix(),
        finds: this.params.filter,
        orders: this.params.order,
      });
    },
    formatTableData(data) {
      // Ubah objek data menjadi array untuk b-table
      return Object.keys(data).map((key) => ({
        parameter: key,
        operator: data[key].operator,
        value: data[key].value,
      }));
    },
  },
  mounted() {
    this.getDevices();
    this.getEventTypes();
    this.getData({
      start: moment(this.params.dateRange.start).unix(),
      end: moment(this.params.dateRange.end).unix(),
      finds: this.params.filter,
      orders: this.params.order,
    });
  },
  created() {
    this.onSearchDevice = debounce(this.onSearchDevice, 300);
  },
};
</script>
